import { useState } from "react";
import { Download, Loader2, PackageOpen } from "lucide-react";
import { toast } from "sonner";

const fileSafe = (name) =>
  (name || "Lesson").replace(/[\\/:*?"<>|]+/g, "").trim() || "Lesson";

/**
 * Phase 103 — Combined Lesson Package PDF download.
 *
 * Renders a single primary CTA that fetches
 *   /api/activities/{id}/export?format=package[&lesson_index=N]
 * and saves the combined PDF (lesson plan + teacher plan + reading +
 * student sheets, stitched into one file with a unified footer band).
 *
 * Props:
 * - activity: the saved Activity. We read `id` + `activity_name`.
 * - lessonIndex: 1-based index inside a unit. Pass `null` for a
 *   single-lesson activity.
 * - variant: "primary" (default — full-width mint pill) or "compact"
 *   (sm size for inline placement next to ExportButtons).
 */
export const LessonPackageButton = ({
  activity,
  lessonIndex = null,
  variant = "primary",
}) => {
  const [busy, setBusy] = useState(false);
  if (!activity?.id) return null;

  const handleDownload = async () => {
    setBusy(true);
    try {
      const api = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({ format: "package" });
      if (lessonIndex !== null && lessonIndex !== undefined) {
        params.set("lesson_index", String(lessonIndex));
      }
      const res = await fetch(
        `${api}/api/activities/${activity.id}/export?${params.toString()}`,
        { credentials: "include" }
      );
      if (!res.ok) {
        const detail = (await res.json().catch(() => null))?.detail || `HTTP ${res.status}`;
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        lessonIndex !== null && lessonIndex !== undefined
          ? `${fileSafe(activity.activity_name)} - Lesson ${lessonIndex} - Lesson Package.pdf`
          : `${fileSafe(activity.activity_name)} - Lesson Package.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Lesson package downloaded.");
    } catch (e) {
      toast.error(`Couldn't download: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (variant === "compact") {
    return (
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy}
        data-testid={
          lessonIndex !== null && lessonIndex !== undefined
            ? `download-lesson-package-${lessonIndex}`
            : "download-lesson-package"
        }
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1c2d3d] text-white font-bold text-xs hover:bg-[#0f1c28] transition disabled:opacity-60 disabled:cursor-wait"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageOpen className="w-3.5 h-3.5" />}
        {busy ? "Building…" : "Download Package PDF"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={busy}
      data-testid={
        lessonIndex !== null && lessonIndex !== undefined
          ? `download-lesson-package-primary-${lessonIndex}`
          : "download-lesson-package-primary"
      }
      className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#1c2d3d] text-white font-extrabold text-sm hover:bg-[#0f1c28] transition shadow-lg shadow-[#1c2d3d]/15 disabled:opacity-60 disabled:cursor-wait"
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      {busy ? "Building Package…" : "Download Complete Lesson Package — PDF"}
    </button>
  );
};

export default LessonPackageButton;
